import axios, { AxiosError } from 'axios';
import type { ApiError } from '../types';

const API_BASE_URL = (import.meta as any).env?.VITE_API_BASE_URL || 'http://localhost:8000';
const AUTH_BASE_URL = (import.meta as any).env?.VITE_AUTH_SERVICE_URL || API_BASE_URL;

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token and admin user context headers
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Inject admin user context headers for gatewayAuthMiddleware & RLS
  const storedUserRaw = localStorage.getItem('admin_user');
  if (storedUserRaw) {
    try {
      const adminUser = JSON.parse(storedUserRaw);
      if (adminUser?.id) config.headers['x-user-id'] = adminUser.id;
      if (adminUser?.role) config.headers['x-user-role'] = adminUser.role;
      if (adminUser?.email) config.headers['x-user-email'] = adminUser.email;
      config.headers['x-is-admin'] = 'true';
    } catch {
      config.headers['x-is-admin'] = 'true';
    }
  } else if (token) {
    config.headers['x-is-admin'] = 'true';
  }

  // Redirect auth requests if needed
  if (config.url && config.url.startsWith('/auth/')) {
    config.baseURL = AUTH_BASE_URL;
  }

  // Rewrite frontend /admin/users to /auth/admin/users (auth-service)
  if (config.url && config.url.startsWith('/admin/users')) {
    config.baseURL = AUTH_BASE_URL;
    config.url = config.url.replace('/admin/users', '/auth/admin/users');
  }

  // Rewrite frontend /admin/subscriptions to /portal/api/subscriptions (core-api-service)
  if (config.url && config.url.startsWith('/admin/subscriptions')) {
    config.url = config.url.replace('/admin/subscriptions', '/portal/api/subscriptions');
  }

  // Rewrite frontend /admin/analytics paths to the correct Kong route (/analytics)
  if (config.url && config.url.startsWith('/admin/analytics/')) {
    config.url = config.url.replace('/admin/analytics/', '/analytics/');
  }

  // Rewrite frontend /admin/clients to /portal/api/clients (core-api-service)
  if (config.url && config.url.startsWith('/admin/clients')) {
    config.url = config.url.replace('/admin/clients', '/portal/api/clients');
  }

  // Rewrite frontend /admin/products/:id/plans to /portal/api/products/:id/plans (core-api-service)
  if (config.url && config.url.match(/^\/admin\/products\/[^/]+\/plans/)) {
    config.url = config.url.replace('/admin/products', '/portal/api/products');
  }

  // Rewrite frontend /admin/plans/:id to /portal/api/plans/:id (core-api-service)
  if (config.url && config.url.startsWith('/admin/plans')) {
    config.url = config.url.replace('/admin/plans', '/portal/api/plans');
  }

  // Rewrite frontend /admin/payment-methods to /portal/api/portal/admin/products/payment-methods
  if (config.url && config.url.startsWith('/admin/payment-methods')) {
    if (config.url === '/admin/payment-methods') {
      config.url = '/portal/api/portal/admin/products/payment-methods/list';
    } else {
      config.url = config.url.replace(
        '/admin/payment-methods',
        '/portal/api/portal/admin/products/payment-methods'
      );
    }
  }

  // Rewrite frontend /admin/alerts to /portal/api/portal/admin/products/alerts/list
  if (config.url && config.url.startsWith('/admin/alerts')) {
    config.url = config.url.replace(
      '/admin/alerts',
      '/portal/api/portal/admin/products/alerts/list'
    );
  }

  // Rewrite frontend /admin/mappings/preview to /portal/api/portal/admin/transformations/preview
  if (config.url && config.url.startsWith('/admin/mappings/preview')) {
    config.url = '/portal/api/portal/admin/transformations/preview';
  }

  // Rewrite frontend /admin/notification-templates to /notifications/api/templates
  if (config.url && config.url.startsWith('/admin/notification-templates')) {
    config.url = config.url.replace(
      '/admin/notification-templates',
      '/notifications/api/templates'
    );
  }

  // Rewrite frontend /admin/configuration to /integration-admin/api/admin/config
  if (config.url && config.url.startsWith('/admin/configuration')) {
    config.url = config.url.replace('/admin/configuration', '/integration-admin/api/admin/config');
  }

  // Custom rewrites for Product endpoints (since they are split across two routers in backend)
  if (config.url && config.url.startsWith('/portal/admin/products/')) {
    if (config.url.includes('/endpoints')) {
      // route to admin-endpoints.ts router
      config.url = config.url.replace(
        '/portal/admin/products/',
        '/portal/api/portal/admin/endpoints/'
      );
    } else {
      // route to admin-products.ts router
      config.url = config.url.replace(
        '/portal/admin/products/',
        '/portal/api/portal/admin/products/'
      );
    }
  }

  // Rewrite frontend /admin/products, endpoints, etc. to /portal/api/portal/admin/... (core-api-service)
  else if (
    config.url &&
    (config.url.startsWith('/admin/products') ||
      config.url.startsWith('/admin/endpoints') ||
      config.url.startsWith('/admin/transformations') ||
      config.url.startsWith('/portal/admin/transformations') ||
      config.url.startsWith('/admin/kong') ||
      config.url.startsWith('/portal/admin/kong') ||
      config.url.startsWith('/portal/admin/endpoints') ||
      config.url.startsWith('/admin/sandbox/mocks'))
  ) {
    if (config.url.startsWith('/portal/admin/')) {
      config.url = config.url.replace('/portal/admin/', '/portal/api/portal/admin/');
    } else {
      config.url = config.url.replace('/admin/', '/portal/api/portal/admin/');
    }
  }

  // Rewrite frontend /admin/support/tickets to /portal/api/support-tickets
  if (config.url && config.url.startsWith('/admin/support/tickets')) {
    config.url = config.url.replace('/admin/support/tickets', '/portal/api/support-tickets');
  }

  // Rewrite frontend /portal/admin/cors-requests to /portal/api/portal/admin/cors-requests
  if (config.url && config.url.startsWith('/portal/admin/cors-requests')) {
    config.url = config.url.replace(
      '/portal/admin/cors-requests',
      '/portal/api/portal/admin/cors-requests'
    );
  }

  // Rewrite frontend /admin/integration-routes to integration-engine /integration-admin/api/routes
  if (config.url && config.url.startsWith('/admin/integration-routes')) {
    config.url = config.url.replace('/admin/integration-routes', '/integration-admin/api/routes');
  }

  // Rewrite frontend /admin/api-logs to core-api-service /portal/api/logs/api
  if (config.url && config.url.startsWith('/admin/api-logs')) {
    config.url = config.url.replace('/admin/api-logs', '/portal/api/logs/api');
  }

  // Rewrite frontend /admin/audit-logs to core-api-service /portal/api/logs/audit
  if (config.url && config.url.startsWith('/admin/audit-logs')) {
    config.url = config.url.replace('/admin/audit-logs', '/portal/api/logs/audit');
  }

  return config;
});

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiError>) => {
    const originalRequest = error.config;

    // If 401 and not already retrying, try to refresh token
    if (error.response?.status === 401 && originalRequest && !(originalRequest as any)._retry) {
      const refreshToken = localStorage.getItem('admin_refresh_token');

      if (refreshToken) {
        try {
          const response = await axios.post(`${AUTH_BASE_URL}/auth/refresh`, {
            refresh_token: refreshToken,
          });

          const { token, refresh_token } = response.data.data;
          localStorage.setItem('admin_auth_token', token);
          localStorage.setItem('admin_refresh_token', refresh_token);

          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${token}`;
          (originalRequest as any)._retry = true;
          return api(originalRequest);
        } catch (refreshError) {
          // Refresh failed, clear tokens and redirect to login
          localStorage.removeItem('admin_auth_token');
          localStorage.removeItem('admin_refresh_token');
          localStorage.removeItem('admin_user');
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      } else {
        // No refresh token, clear and redirect to login
        localStorage.removeItem('admin_auth_token');
        localStorage.removeItem('admin_refresh_token');
        localStorage.removeItem('admin_user');
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

export const getErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const apiError = error.response?.data as ApiError | undefined;
    return apiError?.error?.message || error.message || 'An unexpected error occurred';
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
};
